/**
 * Reading and writing JSON in `localStorage`, safely.
 *
 * Every access is wrapped. `localStorage` is not merely *empty* in a private
 * window or with site data blocked — the accessor itself **throws**, and an
 * unguarded read at module scope takes the whole app down on load for the
 * subset of users who have that setting. This is also why nothing here is
 * allowed to be load-bearing: what lives in storage is a saved table layout and
 * a theme choice, never state the product needs to be correct.
 */

import { storageKey } from '../config/app-config';

/**
 * Read and parse a namespaced key.
 *
 * Returns `null` for absent, unparseable, or unreadable — the caller cannot
 * tell them apart and does not need to, because the answer in all three cases
 * is "use the default".
 */
export function loadJSON<T>(suffix: string): T | null {
  try {
    const raw = window.localStorage.getItem(storageKey(suffix));
    if (raw === null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/** Write a namespaced key. Silently does nothing if storage is unavailable. */
export function saveJSON(suffix: string, value: unknown): void {
  try {
    window.localStorage.setItem(storageKey(suffix), JSON.stringify(value));
  } catch {
    // Also the quota path: a saved layout that will not fit is not worth an
    // error dialog, and there is nothing the user could do about it.
  }
}

export function removeJSON(suffix: string): void {
  try {
    window.localStorage.removeItem(storageKey(suffix));
  } catch {
    // Nothing to do.
  }
}

/**
 * Every saved key for this build, so "reset my layouts" can exist.
 *
 * Without it, a user whose table layout has become unusable has no way back
 * except clearing site data for the whole origin — which also signs them out.
 */
export function listKeys(prefix: string): string[] {
  try {
    const full = storageKey(prefix);
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(full)) keys.push(key);
    }
    return keys;
  } catch {
    return [];
  }
}

/** Remove every saved key under a prefix. */
export function clearPrefix(prefix: string): number {
  const keys = listKeys(prefix);
  for (const key of keys) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Nothing to do.
    }
  }
  return keys.length;
}
